/**
 * Module dependencies.
 */

import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '../input/input';
import { Select } from '../select/select';
import { FormField } from '../../form-field/form-field';
import { ErrorField } from '../../error-field/error-field';
import styles from './phone-number.module.less';
import classnames from 'classnames';
import { DialCode } from 'app/types/dial-code';
import { parsePhoneNumber, formatPhoneNumber, findDialCodeId, getDialCodeNumber } from 'app/core/utils/phone-utils';
import { resolveAppUrl } from 'app/core/utils/url-resolver';

/**
 * Export `PhoneNumberProps` type.
 */

export type PhoneNumberProps = {
  value?: string;
  onChange?: (value: string) => void;
  dialCodes: DialCode[];
  defaultDialCode?: string;
  disabled?: boolean;
  error?: any;
  label: string;
  placeholder?: string;
  invalid?: boolean;
  isTouched?: boolean;
  className?: string;
  name: string;
};

/**
 * `PhoneNumberComponent` component.
 */

function PhoneNumberComponent(props: PhoneNumberProps, forwardedRef: any): JSX.Element {
  const {
    value,
    onChange,
    dialCodes = [],
    defaultDialCode,
    disabled,
    error,
    label,
    placeholder,
    invalid,
    isTouched,
    className
  } = props;

  // Internal state
  const [dialCodeId, setDialCodeId] = useState<string | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  // Find default dial code ID
  const defaultDialCodeId = useMemo(() => {
    if (!defaultDialCode || !dialCodes.length) {
      return undefined;
    }

    return dialCodes.find(dc => dc.code === defaultDialCode)?.id;
  }, [defaultDialCode, dialCodes]);

  // Parse value when it changes (for edit mode)
  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumber(value);

      if (parsed) {
        const foundDialCodeId = findDialCodeId(dialCodes, parsed.dialCode);

        if (foundDialCodeId) {
          setDialCodeId(foundDialCodeId);
          setPhoneNumber(parsed.phone);

          return;
        }
      }
    }

    // If no value or couldn't parse, set default
    if (!dialCodeId && defaultDialCodeId) {
      setDialCodeId(defaultDialCodeId);
    }
  }, [value, dialCodes, dialCodeId, defaultDialCodeId]);

  // Handle dial code change
  const handleDialCodeChange = useCallback(
    (newDialCodeId: string) => {
      setDialCodeId(newDialCodeId);

      // Emit new formatted value
      if (onChange) {
        if (!newDialCodeId) {
          // If dial code is cleared, emit empty string
          onChange('');
          return;
        }

        const dialCodeNumber = getDialCodeNumber(dialCodes, newDialCodeId);

        if (dialCodeNumber) {
          if (phoneNumber) {
            // If phone number exists, emit formatted value
            onChange(formatPhoneNumber(dialCodeNumber, phoneNumber));
          } else {
            // If no phone number yet, emit just the dial code with +
            onChange(`+${dialCodeNumber}`);
          }
        }
      }
    },
    [dialCodes, onChange, phoneNumber]
  );

  // Handle phone number change
  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPhone = e.target.value;

      setPhoneNumber(newPhone);

      // Emit new formatted value
      if (onChange) {
        if (!newPhone) {
          // If phone is cleared, emit empty string or just dial code
          if (dialCodeId) {
            const dialCodeNumber = getDialCodeNumber(dialCodes, dialCodeId);
            onChange(dialCodeNumber ? `+${dialCodeNumber}` : '');
          } else {
            onChange('');
          }
          return;
        }

        if (dialCodeId) {
          const dialCodeNumber = getDialCodeNumber(dialCodes, dialCodeId);

          if (dialCodeNumber) {
            onChange(formatPhoneNumber(dialCodeNumber, newPhone));
          }
        }
      }
    },
    [dialCodes, dialCodeId, onChange]
  );

  // Prepare dial code options
  const dialCodeOptions = useMemo(
    () =>
      dialCodes.map(dc => {
        // Ensure dialCode starts with +
        const displayCode = dc.dialCode.startsWith('+') ? dc.dialCode : `+${dc.dialCode}`;

        return {
          value: dc.id,
          // Label para a busca (mostra país + código)
          label: (
            <div className={styles.dialCodeOption}>
              <img className={styles.flagImage} src={resolveAppUrl('flags', { interpolations: { code: dc.code } })} />
              <span className={styles.countryName}>{dc.name}</span>
              <span className={styles.dialCodeText}>{displayCode}</span>
            </div>
          ),
          searchValue: `${dc.name} ${dc.dialCode}`,
          // Minimal para aparecer na box (mostra bandeira + código)
          minimal: (
            <div className={styles.dialCodeMinimal}>
              <img
                className={styles.flagImageSmall}
                src={resolveAppUrl('flags', { interpolations: { code: dc.code } })}
              />
              <span className={styles.dialCodeValue}>{displayCode}</span>
            </div>
          )
        };
      }),
    [dialCodes]
  );

  return (
    <FormField className={className} label={label}>
      <div className={styles.wrapper} ref={forwardedRef}>
        <Select
          className={classnames(styles.dialCodeSelect, {
            [styles.disabled]: disabled
          })}
          disabled={disabled}
          dropdownStyle={{ minWidth: '300px' }}
          fieldClassName={styles.selectField}
          filterOption={(input, option) => {
            const searchValue = (option as any)?.searchValue || '';
            return searchValue.toLowerCase().includes(input.toLowerCase());
          }}
          name={'dialCode'}
          onChange={handleDialCodeChange}
          optionLabelProp={'minimal'}
          options={dialCodeOptions}
          placeholder={'+'}
          showSearch
          value={dialCodeId}
        />

        <Input
          className={styles.phoneInput}
          disabled={disabled}
          error={undefined}
          fieldClassName={styles.inputField}
          inputType={'input'}
          label={''}
          name={'phone'}
          onChange={handlePhoneNumberChange}
          placeholder={placeholder ?? label}
          value={phoneNumber}
        />
      </div>

      <ErrorField error={error} isVisible={invalid || isTouched} />
    </FormField>
  );
}

/**
 * Export `PhoneNumber` component.
 */

export const PhoneNumber = forwardRef<any, PhoneNumberProps>(PhoneNumberComponent);
